// Margin Control (FM-872) — GroupMarginRateConfiguration contracts.

export enum MarginExposureMode {
  NewExposureOnly = 0, // "Initial"
  NewAndExisting = 1, // "Initial and Maintenance"
}

export enum MarginRateApplyType {
  Set = 0, // "Margin Rate Set"
  Multiply = 1, // "Margin Multiply"
}

export enum GroupMarginRateConfigurationStatus {
  Applied = 1,
  PartiallyUndone = 2,
  Undone = 3,
  Backup = 4,
  Plan = 5,
}

export enum GroupMarginRateHistoryEventType {
  Applied = 1,
  Undone = 2,
  PartiallyUndone = 3,
  Backup = 4,
}

export interface ResultDto<T> {
  value: T;
  text: string;
}

export interface MarginControlPageStateDto {
  applyEnabled: boolean;
  applyDisabledReason: string;
}

export interface MarginControlOptionsDto {
  modes: ResultDto<number>[];
  applyTypes: ResultDto<number>[];
  statuses: ResultDto<number>[];
}

export interface FetchMarginRatesInput {
  skipCount?: number;
  maxResultCount?: number;
  sorting?: string;
  draw?: number;
  groupFilter?: string | null;
  symbolFilter?: string | null;
}

export interface MarginRateRowDto {
  selected: boolean;
  group: string;
  path: string;
  resolvedSymbol: string;
  marginRateInitialBuy: number | null;
  marginRateInitialSell: number | null;
  marginRateMaintenanceBuy: number | null;
  marginRateMaintenanceSell: number | null;
  effectiveInitialBuy: number | null;
  effectiveInitialSell: number | null;
  effectiveMaintenanceBuy: number | null;
  effectiveMaintenanceSell: number | null;
  initialBuySource: string;
  initialSellSource: string;
  maintenanceBuySource: string;
  maintenanceSellSource: string;
  marginMode: number;
  demoLeverage: number;
  rowKey: string;
}

export interface MarginRateSelectionKeyDto {
  group: string;
  path: string;
  expectedEffectiveInitialBuy: number | null;
  expectedEffectiveInitialSell: number | null;
  expectedEffectiveMaintenanceBuy: number | null;
  expectedEffectiveMaintenanceSell: number | null;
  expectedMarginRateInitialBuy: number | null;
  expectedMarginRateInitialSell: number | null;
  expectedMarginRateMaintenanceBuy: number | null;
  expectedMarginRateMaintenanceSell: number | null;
}

export type ApplyMarginRateItemDto = MarginRateSelectionKeyDto;

export interface PreviewMarginRatesInput {
  mode: number;
  applyType: number;
  rate: number;
  previewLogin: number | null;
  orderType: number;
  items: MarginRateSelectionKeyDto[];
}

export type ApplyMarginRatesInput = PreviewMarginRatesInput;

export interface MarginRatePreviewRowDto {
  group: string;
  path: string;
  resolvedSymbol: string;
  effectiveInitialBuy: number | null;
  effectiveInitialSell: number | null;
  effectiveMaintenanceBuy: number | null;
  effectiveMaintenanceSell: number | null;
  proposedInitialBuy: number | null;
  proposedInitialSell: number | null;
  proposedMaintenanceBuy: number | null;
  proposedMaintenanceSell: number | null;
  stale: boolean;
  staleReason: string;
  modeNote: string;
  rowKey: string;
  marginForOneLotCurrent: number | null;
  marginForOneLotProposedEstimate: number | null;
  marginCurrency: string;
  marginCheckMessage: string;
  marginCheckSuccess: boolean;
  previewLoginUsed: number | null;
  previewLoginAutoSelected: boolean;
  loginLeverage: number | null;
  symbolCalcMode: number | null;
  symbolCalcModeName: string;
  checkSymbol: string;
  checkPrice: number | null;
}

export interface ApplyMarginRateRowResultDto {
  group: string;
  path: string;
  resolvedSymbol: string;
  success: boolean;
  message: string;
  marginForOneLotAfter: number | null;
  marginCurrency: string;
  marginCheckMessage: string;
}

export interface ApplyMarginRatesResultDto {
  configurationId: string | null;
  configurationName: string;
  items: ApplyMarginRateRowResultDto[];
}

export interface FetchMarginRateConfigurationsInput {
  skipCount?: number;
  maxResultCount?: number;
  sorting?: string;
  draw?: number;
  nameFilter?: string | null;
  status?: number | null;
}

export interface MarginRateConfigurationListItemDto {
  name: string;
  mode: number;
  modeLabel: string;
  applyType: number;
  applyTypeLabel: string;
  rate: number;
  status: number;
  statusLabel: string;
  itemCount: number;
  successCount: number;
  failureCount: number;
  appliedBy: string;
  appliedAt: string;
  id: string;
  canUndo: boolean;
  canApplyBackup: boolean;
}

export interface TakeMarginRateBackupResultDto {
  configurationId: string;
  configurationName: string;
  itemCount: number;
}

export interface ApplyMarginRateBackupInput {
  configurationId: string;
}

export interface MarginRatePlanListItemDto {
  id: string;
  name: string;
}

export interface SaveMarginRatePlanItemDto {
  group: string;
  path: string;
}

export interface SaveMarginRatePlanInput {
  name: string;
  mode: number;
  applyType: number;
  rate: number;
  items: SaveMarginRatePlanItemDto[];
}

export interface SaveMarginRatePlanResultDto {
  planId: string;
  name: string;
  itemCount: number;
}

export interface LoadMarginRatePlanResultDto {
  planId: string;
  name: string;
  mode: number;
  applyType: number;
  rate: number;
  missingCount: number;
  items: MarginRateRowDto[];
}

export interface UndoMarginRateRowResultDto {
  group: string;
  path: string;
  success: boolean;
  skipped: boolean;
  message: string;
}

export interface UndoMarginRatesResultDto {
  configurationId: string;
  restoredCount: number;
  skippedCount: number;
  failedCount: number;
  statusLabel: string;
  items: UndoMarginRateRowResultDto[];
}

export interface FetchMarginRateHistoryInput {
  skipCount?: number;
  maxResultCount?: number;
  sorting?: string;
  draw?: number;
  startDate?: string | null;
  endDate?: string | null;
  configurationId?: string | null;
}

export interface MarginRateHistoryEventDto {
  occurredAt: string;
  eventType: number;
  eventTypeLabel: string;
  summary: string;
  actor: string;
  configurationId: string | null;
  id: string;
}
